const API_BASE_URL = (import.meta.env.DEV && import.meta.env.VITE_USE_API_PROXY)
  ? ''
  : (import.meta.env.VITE_API_GATEWAY_URL || 'http://moneybridge-feature-api.australiaeast.azurecontainer.io:8000')

export interface LoanProduct {
  id: string
  product_name: string
  product_type: string
  interest_rate: number
  comparison_rate: number
  max_lvr: number
  fees: {
    application_fee?: number
    annual_fee?: number
    discharge_fee?: number
  }
  features: {
    offset_account?: boolean
    redraw?: boolean
    extra_payments?: boolean
  }
}

export type PolicyMatch = 'eligible' | 'may_be_eligible'
export type PolicyFilters = Partial<Record<string, string[]>>

export interface CompareRequest {
  loan_amount: number
  property_value: number
  loan_term_years: number
  filters?: PolicyFilters
}

export interface ComparisonResult {
  id?: string
  product_name: string
  product_type: string
  interest_rate: number
  comparison_rate: number
  monthly_payment: number
  total_cost: number
  total_interest: number
  total_fees: number
  fees: Record<string, number>
  features: Record<string, boolean>
  policy_tags?: Record<string, string[]>
  policy_match?: PolicyMatch
  eligible: boolean
}

export interface CompareResponse {
  search_criteria?: {
    loan_amount: number
    property_value: number
    loan_term_years: number
    lvr: number
  }
  result_count: number
  eligible_count?: number
  may_be_eligible_count?: number
  results: ComparisonResult[]
  error?: string
}

const COMPARE_TIMEOUT_MS = 15000

export const loanApi = {
  // Fire-and-forget: wake the backend on page load to avoid cold-start hang
  warmup(): void {
    fetch(`${API_BASE_URL}/api/loans/products`).catch(() => { /* silent */ })
  },

  async getProducts(): Promise<{ count: number; products: LoanProduct[] }> {
    const response = await fetch(`${API_BASE_URL}/api/loans/products`)
    if (!response.ok) {
      throw new Error('Failed to fetch loan products')
    }
    return response.json()
  },

  async compareLoans(request: CompareRequest): Promise<CompareResponse> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), COMPARE_TIMEOUT_MS)

    try {
      const response = await fetch(`${API_BASE_URL}/api/loans/compare`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
        signal: controller.signal,
      })
      if (!response.ok) {
        throw new Error('Failed to compare loans')
      }
      return response.json()
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        throw new Error('TIMEOUT')
      }
      throw err
    } finally {
      clearTimeout(timeoutId)
    }
  },
}
